import React, { useCallback, useMemo, useRef, useState } from "react";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useParams } from "next/navigation";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../DataTable";
import { PageChanger } from "../PageChanger";
import { SearchBar } from "../SearchBar";
import { timeAgo } from "@/utils/generic";
import { useSpotUser } from "@/utils/spotUser";
import {
  Assistant,
  AgentToolSettingsInput,
  AssistantsService,
} from "@/generated";
import { useQuery } from "@tanstack/react-query";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { ChevronLeftIcon, SearchX } from "lucide-react";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { TruncatedTextWithTooltip } from "../TruncatedTextWithTooltip";
import { AnimatePresence, motion } from "framer-motion";
import BaseDialog from "../Modals/BaseDialog";
import { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import { ToolConfigStep } from "./ToolConfigStep";
import {
  AssistantVersionContext,
  AssistantVersionInput,
} from "@/components/Form/AssistantVersionField";
import { CURRENT_VERSION, versionIdForApi } from "@/utils/versions";

const PAGE_SIZE = 8;

const columnHelper = createColumnHelper<Assistant>();

const columns: ColumnDef<Assistant, any>[] = [
  columnHelper.accessor("name", {
    cell: (info) => {
      return (
        <div className="flex gap-2 items-center">
          <NoxusIcon icon={info.row.original?.image_url || "coworker"} />
          <TruncatedTextWithTooltip>{info.getValue()}</TruncatedTextWithTooltip>
        </div>
      );
    },
    header: "Name",
  }),
  columnHelper.accessor("description", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue() || "-"}
      </p>
    ),
    header: "Description",
  }),
  columnHelper.accessor("last_updated_at", {
    cell: (info) => (
      <p className="text-sm text-muted-foreground truncate">
        {info.getValue() ? timeAgo(info.getValue()) : "-"}
      </p>
    ),
    header: "Created",
  }),
];

interface AgentPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAgent: (props: {
    agent_id: string;
    version_id: string | null;
    config: Record<string, unknown>;
  }) => void;
  openAgents: {
    id: string;
    name: string;
  }[];
  editingTool?: AgentToolSettingsInput;
  currentAssistantId?: string;
  itemConfig?: Record<string, NoxusFormEntryConfig>;
}

export const AgentPickerModal = ({
  isOpen,
  onClose,
  onSelectAgent,
  openAgents,
  editingTool,
  itemConfig,
}: AgentPickerProps) => {
  const { spotGroup } = useSpotUser();

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // memoize assistantId
  const params = useParams();
  const currentAssistantId = useMemo(() => {
    return params?.assistantId as string | undefined;
  }, [params]);

  const filterAgentIds = useMemo(() => {
    return (
      openAgents
        ?.map((agent) => {
          if (agent.id === currentAssistantId) {
            return undefined;
          }
          return agent.id;
        })
        .filter((id) => id !== undefined) || []
    );
  }, [openAgents, currentAssistantId]);

  const { data: agentsData, isLoading: isLoadingAgents } = useQuery({
    queryKey: [DATA_KEYS.ASSISTANTS, currentPage, filterAgentIds, search],
    queryFn: () =>
      AssistantsService.getAssistants({
        path: { group_id: spotGroup.id },
        query: {
          size: PAGE_SIZE,
          query: search || undefined,
          page: currentPage,
        },
      }),
  });

  const filteredAgentsData = useMemo(() => {
    if (!agentsData) return null;

    const filteredItems = agentsData.data.items.filter(
      (agent: Assistant) =>
        !filterAgentIds.includes(agent.id) && agent.id !== currentAssistantId
    );

    return {
      ...agentsData,
      data: {
        ...agentsData.data,
        items: filteredItems,
      },
    };
  }, [agentsData, filterAgentIds, currentAssistantId]);

  const { data: editingAgentData } = useRemoteData(
    DATA_KEYS.ASSISTANT_ID,
    {
      path: {
        group_id: spotGroup.id,
        assistant_id: editingTool?.agent_id || "",
      },
    },
    { staleTime: Infinity, enabled: !!editingTool?.agent_id }
  );

  const handleBack = () => {
    setSelectedAgentId(null);
    setSelectedVersionId(CURRENT_VERSION);
  };

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    editingTool?.agent_id || null
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    editingTool?.version_id || CURRENT_VERSION
  );
  const [isVersionInvalid, setIsVersionInvalid] = useState(false);

  const [agentName, agentIcon] = useMemo(() => {
    if (!selectedAgentId) return [null, null];

    let agent: Assistant | undefined;

    if (editingAgentData) {
      agent = editingAgentData;
    } else {
      agent = filteredAgentsData?.data.items.find(
        (a: Assistant) => a.id === selectedAgentId
      );
    }

    return [agent?.name, agent?.image_url || "coworker"];
  }, [selectedAgentId, filteredAgentsData, editingAgentData]);

  const toolConfigRef = useRef<Record<string, unknown>>({
    extra_instructions: editingTool?.extra_instructions ?? "",
    tool_display_message_start:
      editingTool?.tool_display_message_start ?? "Calling co-worker",
    tool_display_message_end:
      editingTool?.tool_display_message_end ?? "Co-worker responded",
    max_clarification_turns: editingTool?.max_clarification_turns ?? 2,
  });

  const handleToolConfigChange = useCallback(
    (values: Record<string, unknown>) => {
      toolConfigRef.current = values;
    },
    []
  );

  const handleSave = () => {
    if (selectedAgentId) {
      onSelectAgent({
        agent_id: selectedAgentId,
        version_id: versionIdForApi(selectedVersionId),
        config: toolConfigRef.current,
      });
      onClose();
    }
  };

  const footerActions = selectedAgentId
    ? [
        ...(editingTool
          ? []
          : [
              {
                label: "Back",
                props: {
                  variant: "ghost",
                  onClick: handleBack,
                  leftIcon: <ChevronLeftIcon size={12} />,
                },
              },
            ]),
        {
          label: editingTool ? "Save" : "Add agent",
          props: {
            onClick: handleSave,
            colorScheme: "blue",
            disabled: !selectedAgentId || isVersionInvalid,
          },
        },
      ]
    : [
        {
          label: "Cancel",
          props: {
            variant: "ghost",
            onClick: onClose,
          },
        },
      ];

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      size="5xl"
      title={
        agentName ? (
          <div className="flex items-center gap-2">
            <NoxusIcon icon={agentIcon || undefined} />
            {agentName}
          </div>
        ) : (
          "Add Agent"
        )
      }
      footerActions={footerActions}
    >
      <div className="-m-1 overflow-x-hidden p-1">
        <AnimatePresence mode="wait" initial={false}>
          {!selectedAgentId ? (
            <motion.div
              key="step1"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div>
                <div className="mb-2">
                  <SearchBar
                    placeholder="Search agents"
                    setValue={setSearch}
                    value={search}
                    isDebounced
                    autoFocus
                  />
                </div>

                <div className="w-full">
                  <DataTable
                    isLoading={isLoadingAgents}
                    columns={columns}
                    data={filteredAgentsData?.data.items || []}
                    rowOnClick={(id, value) => {
                      setSelectedAgentId(id);
                    }}
                    loadingRows={8}
                    noDataMessage="No more agents available"
                    emptyState={
                      search ? (
                        <NoxusEmptyBlock
                          title="No agents"
                          description={
                            <>No agents match &ldquo;{search}&rdquo;.</>
                          }
                          icon={SearchX}
                          size="sm"
                          button="Clear search"
                          buttonVariant="outline"
                          onClick={() => setSearch("")}
                        />
                      ) : undefined
                    }
                    noShadow
                  />

                  {!!filteredAgentsData &&
                    !!filteredAgentsData?.data.pages &&
                    filteredAgentsData.data.pages > 1 && (
                      <footer className="w-full pt-6 flex justify-center">
                        <PageChanger
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          totalPages={filteredAgentsData?.data.pages || 0}
                          maxWidth="3rem"
                          size="sm"
                        />
                      </footer>
                    )}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ type: "tween", duration: 0.3 }}
              style={{ width: "100%" }}
            >
              <div className="mb-6">
                <AssistantVersionContext.Provider value={selectedAgentId}>
                  <AssistantVersionInput
                    label="Version"
                    value={selectedVersionId}
                    onChange={setSelectedVersionId}
                    allowCurrentVersion
                    disableDeselection
                    onValidityChange={setIsVersionInvalid}
                  />
                </AssistantVersionContext.Provider>
              </div>
              {itemConfig && (
                <ToolConfigStep
                  itemConfig={itemConfig}
                  defaultValues={toolConfigRef.current}
                  onChange={handleToolConfigChange}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BaseDialog>
  );
};
